// SCR-CAT-08 Customers and SCR-CAT-09 groups (frozen mockups-cat-v1). Owner: tabs Customers · Groups, every action;
// Seller: the list and cards, view only. Invoice figures arrive with P6 (zero until then).
import { useCallback, useMemo, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Badge,
  BottomSheet,
  Button,
  Chips,
  EmptyState,
  Field,
  Filters,
  FormGrid,
  Input,
  List,
  ListRow,
  Notice,
  SearchField,
  Select,
  SheetActions,
  SkeletonList,
  SummaryCard,
  Tabs,
  VirtualList,
} from '../../design/kit';
import {
  saveCustomer,
  saveCustomerGroup,
  toggleCustomer,
  toggleCustomerGroup,
  type Customer,
  type CustomerGroup,
} from '../../data/catalog';
import { Code, Name } from '../../i18n/Isolate';
import type { Language } from '../../i18n';
import { formatDate } from '../../lib/format';
import { useSession } from '../../app/session';
import { useCardHeader } from '../../app/Shell';
import { anonymizeCustomer, anonymizedNumber } from '../../data/advanced';
import { PRE_ORDERS, loadPreOrders } from '../../data/preorders';
import { SALES, loadCollections, loadSales } from '../../data/sales';
import { CollectionRows, InvoiceRows } from '../sales/SalesPage';
import {
  CATALOG,
  initials,
  parseAmount,
  refusalCode,
  useCustomerGroups,
  useCustomers,
  useMoney,
  useRefusalText,
  useTab,
} from '../catalog/common';

const TABS = ['customers', 'groups'] as const;
type Status = 'active' | 'inactive' | 'all';
const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function CustomersPage() {
  const { t } = useTranslation();
  const { role } = useSession();
  const owner = role === 'owner';
  const [tab, setTab] = useTab(TABS, 'customers');
  return (
    <>
      {owner && (
        <Tabs
          label={t('p.customers')}
          value={tab}
          onChange={(v) => setTab(v as (typeof TABS)[number])}
          items={TABS.map((id) => ({ id, label: t(`tab.${id}`) }))}
        />
      )}
      {owner && tab === 'groups' ? <GroupsTab /> : <CustomersTab />}
    </>
  );
}

function CustomersTab() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language as Language;
  const { role } = useSession();
  const owner = role === 'owner';
  const navigate = useNavigate();
  const money = useMoney();
  const customers = useCustomers();
  const groups = useCustomerGroups();
  const [text, setText] = useState('');
  const [group, setGroup] = useState('');
  const [status, setStatus] = useState<Status>('active');
  const [creating, setCreating] = useState(false);
  const all = useMemo(() => customers.data ?? [], [customers.data]);
  const list = useMemo(() => {
    const q = text.trim().toLowerCase();
    const digits = q.replace(/\D/g, '');
    return all.filter(
      (c) =>
        (status === 'all' || c.active === (status === 'active')) &&
        (!group || c.group_id === group) &&
        (!q ||
          c.name.toLowerCase().includes(q) ||
          (c.area ?? '').toLowerCase().includes(q) ||
          (digits.length > 0 && (c.phone ?? '').replace(/\D/g, '').includes(digits))),
    );
  }, [all, text, group, status]);
  const active = all.filter((c) => c.active);

  return (
    <>
      <SummaryCard
        label={t('unpaidsum')}
        value={money(all.reduce((s, c) => s + c.unpaid_total, 0))}
        figures={[
          { label: t('activecust'), value: String(active.length) },
          { label: t('overduesum'), value: money(all.reduce((s, c) => s + c.overdue_total, 0)) },
        ]}
      />
      {(owner || role === 'seller') && (
        <div className="yv-chips">
          <Button pill variant="main" icon="plus" onClick={() => setCreating(true)}>
            {t('newcustomer')}
          </Button>
        </div>
      )}
      <Filters>
        <SearchField value={text} onChange={setText} label={t('search.partners')} />
        <Field label={t('f.group')}>
          {({ id }) => (
            <Select id={id} value={group} onChange={(e) => setGroup(e.target.value)}>
              <option value="">{t('f.allgroups')}</option>
              {(groups.data ?? []).map((g) => (
                <option key={g.id} value={g.id}>
                  {g.name}
                </option>
              ))}
            </Select>
          )}
        </Field>
        <Chips
          label={t('status')}
          items={[
            { id: 'active', label: t('f.active') },
            { id: 'inactive', label: t('f.inactive') },
            { id: 'all', label: t('f.all') },
          ]}
          selected={[status]}
          onToggle={(id) => setStatus(id as Status)}
        />
      </Filters>
      {!customers.data ? (
        <SkeletonList label={t('loading')} />
      ) : list.length === 0 ? (
        <EmptyState icon="users" title={t('nomatch')} text={t('nomatch.text')} />
      ) : (
        <VirtualList
          label={t('p.customers')}
          items={list}
          rowKey={(c) => c.id}
          row={(c) => (
            <ListRow
              testId={`customer-${c.name}`}
              lead={{ initials: initials(c.name) }}
              leadTone={c.overdue_total > 0 ? 'danger' : 'neutral'}
              title={<Name>{customerName(t, c)}</Name>}
              onOpen={() => navigate(`/customers/${c.id}`)}
              sub={
                <>
                  {[c.group_name, c.area].filter(Boolean).map((v, i) => (
                    <span key={i}>
                      <Name>{v}</Name> ·{' '}
                    </span>
                  ))}
                  {c.phone && (
                    <>
                      <Code>{c.phone}</Code> ·{' '}
                    </>
                  )}
                  {c.last_purchase ? t('lastpurchase', { d: formatDate(c.last_purchase, lang) }) : t('nopurchase')}
                </>
              }
              value={c.unpaid_total > 0 ? money(c.unpaid_total) : undefined}
              badge={
                <>
                  {c.overdue_total > 0 && <Badge tone="danger">{t('overduesum')}</Badge>}
                  {c.visibility === 'private' && <Badge>{t('vis.private')}</Badge>}
                  {!c.active && <Badge>{t('inactive')}</Badge>}
                </>
              }
            />
          )}
        />
      )}
      {creating && (
        <CustomerSheet
          customer={null}
          groups={groups.data ?? []}
          onClose={() => setCreating(false)}
          onSaved={(id) => {
            setCreating(false);
            navigate(`/customers/${id}`);
          }}
        />
      )}
    </>
  );
}

type CardTab = 'profile' | 'invoices' | 'collections';
const CARD_TABS: CardTab[] = ['profile', 'invoices', 'collections'];

export function CustomerCard() {
  const { t } = useTranslation();
  const { id = '' } = useParams();
  const { role } = useSession();
  const owner = role === 'owner';
  const navigate = useNavigate();
  const money = useMoney();
  const customers = useCustomers();
  const groups = useCustomerGroups();
  const customer = customers.data?.find((c) => c.id === id);
  const [tab, setTab] = useTab(CARD_TABS, 'profile');
  const [editing, setEditing] = useState(false);
  const [anonymizing, setAnonymizing] = useState(false);
  const back = useCallback(() => navigate('/customers'), [navigate]);
  useCardHeader(customer ? <Name>{customerName(t, customer)}</Name> : t('p.customers'), back);

  if (customers.isLoading) return <SkeletonList label={t('loading')} />;
  if (!customer) return <Notice tone="warning">{t('err.notfound')}</Notice>;
  return (
    <>
      <SummaryCard
        label={t('unpaidsum')}
        value={money(customer.unpaid_total)}
        figures={[
          { label: t('overduesum'), value: money(customer.overdue_total) },
          { label: t('creditlimit'), value: customer.credit_limit > 0 ? money(customer.credit_limit) : '—' },
        ]}
      />
      <Tabs
        label={t('c8.title')}
        value={tab}
        onChange={(v) => setTab(v as CardTab)}
        items={CARD_TABS.map((v) => ({ id: v, label: t(`tab.${v}`) }))}
      />
      {tab === 'profile' && (
        <>
          <List label={t('tab.profile')}>
            <ListRow
              lead={{ icon: 'users' }}
              title={t('f.group')}
              sub={customer.group_name ? <Name>{customer.group_name}</Name> : '—'}
            />
            <ListRow
              lead={{ icon: 'truck' }}
              title={t('area')}
              sub={customer.area ? <Name>{customer.area}</Name> : '—'}
            />
            <ListRow
              lead={{ icon: 'phone' }}
              title={t('phone')}
              value={customer.phone ? <Code>{customer.phone}</Code> : '—'}
            />
            <ListRow
              lead={{ icon: 'users' }}
              title={t('contact')}
              sub={customer.contact ? <Name>{customer.contact}</Name> : '—'}
            />
            {customer.email && (
              <ListRow lead={{ icon: 'info' }} title={t('email')} sub={<Code>{customer.email}</Code>} />
            )}
            <ListRow
              lead={{ icon: 'cash' }}
              title={t('creditlimit')}
              value={customer.credit_limit > 0 ? money(customer.credit_limit) : '—'}
              sub={t('creditlimit.hint')}
            />
            {customer.notes && (
              <ListRow lead={{ icon: 'file' }} title={t('notes')} sub={<Name>{customer.notes}</Name>} />
            )}
            <ListRow
              lead={{ icon: 'info' }}
              title={t('f.visibility')}
              badge={<Badge>{t(customer.visibility === 'private' ? 'vis.private' : 'vis.public')}</Badge>}
            />
            {owner && customer.created_by_name && (
              <ListRow lead={{ icon: 'users' }} title={t('createdby')} sub={<Name>{customer.created_by_name}</Name>} />
            )}
            <ListRow
              lead={{ icon: 'check' }}
              title={t('status')}
              badge={
                customer.anonymized ? (
                  <Badge>{t('ano.badge')}</Badge>
                ) : customer.active ? (
                  <Badge tone="success">{t('active')}</Badge>
                ) : (
                  <Badge>{t('inactive')}</Badge>
                )
              }
            />
          </List>
          {(owner || customer.mine) && !customer.anonymized && (
            <div className="yv-chips">
              <Button pill onClick={() => setEditing(true)}>
                {t('edit.btn')}
              </Button>
              {owner && (
                <Button pill onClick={() => setAnonymizing(true)}>
                  {t('ano.act')}
                </Button>
              )}
            </div>
          )}
          {anonymizing && <AnonymizeSheet customer={customer} onClose={() => setAnonymizing(false)} />}
        </>
      )}
      {tab === 'invoices' && <CustomerInvoices customer={customer.id} />}
      {tab === 'collections' && <CustomerCollections customer={customer.id} />}
      {editing && (
        <CustomerSheet
          customer={customer}
          groups={groups.data ?? []}
          onClose={() => setEditing(false)}
          onSaved={() => setEditing(false)}
        />
      )}
    </>
  );
}

type Errors = Partial<Record<'name' | 'phone' | 'group' | 'email' | 'limit' | 'form', string>>;

function CustomerSheet({
  customer,
  groups,
  onClose,
  onSaved,
}: {
  customer: Customer | null;
  groups: CustomerGroup[];
  onClose: () => void;
  onSaved: (id: string) => void;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const refusal = useRefusalText();
  const [name, setName] = useState(customer?.name ?? '');
  const [group, setGroup] = useState(customer?.group_id ?? '');
  const [area, setArea] = useState(customer?.area ?? '');
  const [phone, setPhone] = useState(customer?.phone ?? '');
  const [contact, setContact] = useState(customer?.contact ?? '');
  const [email, setEmail] = useState(customer?.email ?? '');
  const [limit, setLimit] = useState(customer ? customer.credit_limit.toFixed(2) : '0.00');
  const { role } = useSession();
  const owner = role === 'owner';
  // A seller's new customer starts private; the owner's starts public. Only the owner sets a credit limit.
  const [visibility, setVisibility] = useState<'public' | 'private'>(
    customer?.visibility ?? (owner ? 'public' : 'private'),
  );
  const [notes, setNotes] = useState(customer?.notes ?? '');
  const [errors, setErrors] = useState<Errors>({});
  const done = () => void qc.invalidateQueries({ queryKey: CATALOG });
  // Only active groups can be chosen; a customer keeps its current group even if that group was deactivated.
  const choices = groups.filter((g) => g.active || g.id === customer?.group_id);
  const save = useMutation({
    mutationFn: () =>
      saveCustomer({
        id: customer?.id ?? null,
        name: name.trim(),
        group: group || null,
        area,
        phone,
        contact,
        email: email.trim(),
        creditLimit: parseAmount(limit) ?? 0,
        notes,
        visibility,
      }),
    onSuccess: (r) => {
      done();
      onSaved(r.id);
    },
    onError: (e) => {
      const code = refusalCode(e);
      const field: keyof Errors =
        code === 'NAME_REQUIRED'
          ? 'name'
          : code === 'PHONE_TAKEN'
            ? 'phone'
            : code === 'GROUP_INACTIVE'
              ? 'group'
              : code === 'INVALID_EMAIL'
                ? 'email'
                : code === 'INVALID_VALUE'
                  ? 'limit'
                  : 'form';
      setErrors({ [field]: refusal(e), ...(field !== 'form' ? { form: t('err.formhead') } : {}) });
    },
  });
  const toggle = useMutation({
    mutationFn: () => toggleCustomer(customer?.id ?? '', !customer?.active),
    onSuccess: () => {
      done();
      onClose();
    },
    onError: (e) => setErrors({ form: refusal(e) }),
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    const next: Errors = {};
    if (!name.trim()) next.name = t('err.required');
    if (email.trim() && !EMAIL.test(email.trim())) next.email = t('err.email');
    const l = parseAmount(limit);
    if (l === null || l < 0) next.limit = t('err.number');
    if (Object.keys(next).length) return setErrors({ ...next, form: t('err.formhead') });
    setErrors({});
    save.mutate();
  };
  return (
    <BottomSheet
      title={t(customer ? 'editcustomer' : 'newcustomer')}
      open
      onClose={onClose}
      busy={save.isPending || toggle.isPending}
    >
      <form onSubmit={submit} noValidate className="yv-skeleton-stack">
        {errors.form && <Notice tone="danger">{errors.form}</Notice>}
        <Field label={t('name')} error={errors.name}>
          {({ id, describedBy, invalid }) => (
            <Input
              id={id}
              dir="auto"
              value={name}
              onChange={(e) => setName(e.target.value)}
              aria-describedby={describedBy}
              aria-invalid={invalid}
            />
          )}
        </Field>
        <FormGrid>
          <Field label={t('f.group')} error={errors.group}>
            {({ id }) => (
              <Select id={id} value={group} onChange={(e) => setGroup(e.target.value)}>
                <option value="">{t('none')}</option>
                {choices.map((g) => (
                  <option key={g.id} value={g.id}>
                    {g.name}
                  </option>
                ))}
              </Select>
            )}
          </Field>
          <Field label={t('area')}>
            {({ id }) => <Input id={id} dir="auto" value={area} onChange={(e) => setArea(e.target.value)} />}
          </Field>
        </FormGrid>
        <FormGrid>
          <Field label={t('phone')} error={errors.phone}>
            {({ id, describedBy, invalid }) => (
              <Input
                id={id}
                dir="ltr"
                inputMode="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                aria-describedby={describedBy}
                aria-invalid={invalid}
              />
            )}
          </Field>
          <Field label={t('contact')}>
            {({ id }) => <Input id={id} dir="auto" value={contact} onChange={(e) => setContact(e.target.value)} />}
          </Field>
        </FormGrid>
        <Field label={t('email')} error={errors.email}>
          {({ id, describedBy, invalid }) => (
            <Input
              id={id}
              dir="ltr"
              inputMode="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              aria-describedby={describedBy}
              aria-invalid={invalid}
            />
          )}
        </Field>
        <Field label={t('f.visibility')} hint={t(visibility === 'private' ? 'vis.private.hint' : 'vis.public.hint')}>
          {({ id }) => (
            <Select id={id} value={visibility} onChange={(e) => setVisibility(e.target.value as 'public' | 'private')}>
              <option value="private">{t('vis.private')}</option>
              <option value="public">{t('vis.public')}</option>
            </Select>
          )}
        </Field>
        {owner && (
          <Field label={t('creditlimit')} hint={t('creditlimit.hint')} error={errors.limit}>
            {({ id, describedBy, invalid }) => (
              <Input
                id={id}
                numeric
                inputMode="decimal"
                value={limit}
                onChange={(e) => setLimit(e.target.value)}
                aria-describedby={describedBy}
                aria-invalid={invalid}
              />
            )}
          </Field>
        )}
        <Field label={t('notes')}>
          {({ id }) => <Input id={id} dir="auto" value={notes} onChange={(e) => setNotes(e.target.value)} />}
        </Field>
        {owner && customer && (
          <Button
            variant={customer.active ? 'danger' : 'default'}
            block
            busy={toggle.isPending}
            onClick={() => toggle.mutate()}
          >
            {customer.active ? t('deactivate') : t('activate')}
          </Button>
        )}
        <SheetActions>
          <Button type="submit" variant="main" busy={save.isPending}>
            {t('save')}
          </Button>
          <Button onClick={onClose}>{t('cancel')}</Button>
        </SheetActions>
      </form>
    </BottomSheet>
  );
}

function GroupsTab() {
  const { t } = useTranslation();
  const groups = useCustomerGroups();
  const [sheet, setSheet] = useState<CustomerGroup | 'new' | null>(null);
  return (
    <>
      <div className="yv-chips">
        <Button pill variant="main" icon="plus" onClick={() => setSheet('new')}>
          {t('newgroup')}
        </Button>
      </div>
      {!groups.data ? (
        <SkeletonList label={t('loading')} />
      ) : (
        <Chips
          label={t('tab.groups')}
          items={groups.data.map((g) => ({ id: g.id, label: `${g.name} · ${g.customer_count}` }))}
          selected={groups.data.filter((g) => g.active).map((g) => g.id)}
          onToggle={(id) => setSheet(groups.data?.find((g) => g.id === id) ?? null)}
        />
      )}
      <Notice tone="info">{t('group.hint')}</Notice>
      {sheet && <GroupSheet group={sheet === 'new' ? null : sheet} onClose={() => setSheet(null)} />}
    </>
  );
}

function GroupSheet({ group, onClose }: { group: CustomerGroup | null; onClose: () => void }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const refusal = useRefusalText();
  const [name, setName] = useState('');
  const [error, setError] = useState<string>();
  const done = () => {
    void qc.invalidateQueries({ queryKey: CATALOG });
    onClose();
  };
  const create = useMutation({
    mutationFn: () => saveCustomerGroup(name.trim()),
    onSuccess: done,
    onError: (e) => setError(refusal(e, 'err.groupdup')),
  });
  const toggle = useMutation({
    mutationFn: () => toggleCustomerGroup(group?.id ?? '', !group?.active),
    onSuccess: done,
    onError: (e) => setError(refusal(e)),
  });
  if (group) {
    return (
      <BottomSheet
        title={group.name}
        sub={t('customers.n', { n: group.customer_count })}
        open
        onClose={onClose}
        busy={toggle.isPending}
      >
        {error && <Notice tone="danger">{error}</Notice>}
        <Notice tone="info">{t('group.hint')}</Notice>
        <SheetActions>
          <Button variant={group.active ? 'danger' : 'main'} busy={toggle.isPending} onClick={() => toggle.mutate()}>
            {group.active ? t('deactivate') : t('activate')}
          </Button>
          <Button onClick={onClose}>{t('cancel')}</Button>
        </SheetActions>
      </BottomSheet>
    );
  }
  return (
    <BottomSheet title={t('newgroup')} open onClose={onClose} busy={create.isPending}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (!name.trim()) return setError(t('err.required'));
          create.mutate();
        }}
        noValidate
        className="yv-skeleton-stack"
      >
        <Field label={t('name')} error={error}>
          {({ id, describedBy, invalid }) => (
            <Input
              id={id}
              dir="auto"
              value={name}
              onChange={(e) => setName(e.target.value)}
              aria-describedby={describedBy}
              aria-invalid={invalid}
            />
          )}
        </Field>
        <SheetActions>
          <Button type="submit" variant="main" busy={create.isPending}>
            {t('save')}
          </Button>
          <Button onClick={onClose}>{t('cancel')}</Button>
        </SheetActions>
      </form>
    </BottomSheet>
  );
}

/** SCR-SAL-07: the customer's invoices (a Seller sees their own, RLS). */
function CustomerInvoices({ customer }: { customer: string }) {
  const { t } = useTranslation();
  const sales = useQuery({ queryKey: [...SALES, 'customer', customer], queryFn: () => loadSales({ customer }) });
  if (!sales.data) return <SkeletonList label={t('loading')} />;
  if (sales.data.length === 0) return <EmptyState icon="file" title={t('inv.empty')} text={t('inv.empty.text')} />;
  return <InvoiceRows sales={sales.data} />;
}

function CustomerCollections({ customer }: { customer: string }) {
  const { t } = useTranslation();
  const collections = useQuery({
    queryKey: [...SALES, 'collections', customer],
    queryFn: () => loadCollections(customer),
  });
  if (!collections.data) return <SkeletonList label={t('loading')} />;
  if (collections.data.length === 0)
    return <EmptyState icon="cash" title={t('inv.empty')} text={t('col.empty.text')} />;
  return <CollectionRows collections={collections.data} />;
}

/** CUS-04: an anonymized customer reads "Anonymized customer 7"; any other customer keeps their name. */
export function customerName(t: TFunction, c: { name: string; anonymized: boolean }): string {
  const n = c.anonymized ? anonymizedNumber(c.name) : null;
  return n !== null ? t('ano.name', { n }) : c.name;
}

/** SCR-ADV-06: final; refused while the customer has unpaid invoices or open pre-orders (owner, CUS-04). */
function AnonymizeSheet({ customer, onClose }: { customer: Customer; onClose: () => void }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const refusal = useRefusalText();
  const sales = useQuery({
    queryKey: [...SALES, 'customer', customer.id],
    queryFn: () => loadSales({ customer: customer.id }),
  });
  const pre = useQuery({ queryKey: [...PRE_ORDERS, 'list'], queryFn: loadPreOrders });
  const unpaid = (sales.data ?? []).filter((x) => x.status === 'unpaid').length;
  const open = (pre.data ?? []).filter((x) => x.customer_id === customer.id && x.status === 'open').length;
  const blocked = unpaid > 0 || open > 0;
  const [problem, setProblem] = useState('');
  const save = useMutation({
    mutationFn: () => anonymizeCustomer(customer.id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: CATALOG });
      onClose();
    },
    onError: (e) => setProblem(refusal(e)),
  });
  return (
    <BottomSheet title={`${t('ano.title')} · ${customer.name}`} open onClose={onClose} busy={save.isPending}>
      {!sales.data || !pre.data ? (
        <SkeletonList label={t('loading')} />
      ) : blocked ? (
        <>
          <Notice tone="danger">
            {t('ano.blocked', {
              u: [unpaid > 0 && t('ano.unpaid', { n: unpaid }), open > 0 && t('ano.pre', { n: open })]
                .filter(Boolean)
                .join(' · '),
            })}
          </Notice>
          <SheetActions>
            <Button onClick={onClose}>{t('ano.keep')}</Button>
          </SheetActions>
        </>
      ) : (
        <>
          <List label={t('ano.title')}>
            <ListRow
              lead={{ icon: 'close' }}
              title={t('ano.cleared')}
              sub={t('ano.clearedlist', { n: t('ano.name', { n: '…' }) })}
            />
            <ListRow lead={{ icon: 'check' }} title={t('ano.kept')} sub={t('ano.keptlist')} />
          </List>
          <Notice tone="danger">{t('ano.final')}</Notice>
          {problem && <Notice tone="danger">{problem}</Notice>}
          <SheetActions>
            <Button onClick={onClose}>{t('ano.keep')}</Button>
            <Button variant="danger" busy={save.isPending} onClick={() => save.mutate()}>
              {t('ano.confirm')}
            </Button>
          </SheetActions>
        </>
      )}
    </BottomSheet>
  );
}
